/**
 * WeaveIn.ai Platform - Medicaid Enrollment Form Modal
 * Form for creating new Medicaid enrollment with auto-population from member data
 */

import React, { useState, useEffect } from 'react';
import {
  Save,
  X,
  ArrowRight,
  ArrowLeft,
  Info,
  AlertTriangle,
  Loader2,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  medicaidEnrollmentAPI,
  MedicaidEnrollmentCreate,
} from '@/services/api/medicaidEnrollment';
import { Member } from '@/services/api/members';

import { useAppTranslation } from '@/contexts/TranslationContext';

interface MedicaidEnrollmentFormProps {
  open: boolean;
  member: Member;
  referralId?: string | number; // Optional referral ID for linking enrollment to referral
  onClose: () => void;
  onSuccess: () => void;
}

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

const INCOME_SOURCES = [
  'employment',
  'self_employment',
  'social_security',
  'disability',
  'unemployment',
  'pension',
  'child_support',
  'other',
];

const EXEMPTION_REASONS = [
  'Under 19 years old',
  'Over 65 years old',
  'Pregnant',
  'Medically frail',
  'Primary caregiver',
  'Disability',
  'Student (full-time)',
  'Already working 80+ hours/month',
];

export default function MedicaidEnrollmentForm({
  open,
  member,
  referralId,
  onClose,
  onSuccess,
}: MedicaidEnrollmentFormProps) {
  const { t } = useAppTranslation();
  const [activeStep, setActiveStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form data state (auto-populated from member)
  const [formData, setFormData] = useState<MedicaidEnrollmentCreate>({
    member_id: member.id,
    referral_id: referralId ? parseInt(referralId.toString()) : undefined,
    state_code: 'CA',
    member_info: {
      first_name: member.first_name || '',
      last_name: member.last_name || '',
      date_of_birth: member.dob || member.date_of_birth || '',
      phone_number: member.phone_no || member.phone_number || '',
      email: member.email || '',
      preferred_language: member.language || member.preferred_language || 'English',
    },
    address: {
      street_address: member.street_address || '',
      city: member.city || '',
      state: member.state || 'California',
      zip_code: member.zip_code || '',
    },
    household: {
      household_size: 1,
      number_of_dependents: 0,
    },
    financial: {
      monthly_income: 0,
      income_sources: [],
    },
    work_requirement: {
      is_exempt: false,
    },
  });

  const steps = ['Member Information', 'Address & Household', 'Financial Information', 'Work Requirements'];

  // Auto-populate from member data on mount
  useEffect(() => {
    if (member && open) {
      setFormData((prev) => ({
        ...prev,
        member_id: member.id,
        member_info: {
          first_name: member.first_name || '',
          last_name: member.last_name || '',
          date_of_birth: member.dob || member.date_of_birth || '',
          phone_number: member.phone_no || member.phone_number || '',
          email: member.email || '',
          preferred_language: member.language || member.preferred_language || 'English',
          ssn_last_4: member.medicaid_id?.slice(-4) || '',
        },
        address: {
          street_address: member.street_address || '',
          city: member.city || '',
          state: member.state || 'California',
          zip_code: member.zip_code || '',
        },
      }));
    }
  }, [member, open]);

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);

    try {
      await medicaidEnrollmentAPI.createEnrollment(formData);
      onSuccess();
      handleClose();
    } catch (err: any) {
      console.error('Error creating enrollment:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to create enrollment');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setError(null);
    onClose();
  };

  const updateMemberInfo = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      member_info: {
        ...prev.member_info,
        [field]: value,
      },
    }));
  };

  const updateAddress = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        [field]: value,
      },
    }));
  };

  const updateHousehold = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      household: {
        ...prev.household,
        [field]: value,
      },
    }));
  };

  const updateFinancial = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      financial: {
        ...prev.financial,
        [field]: value,
      },
    }));
  };

  const updateWorkRequirement = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      work_requirement: {
        ...prev.work_requirement!,
        [field]: value,
      },
    }));
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        // Member Information
        return (
          <div className="grid grid-cols-1 gap-4">
            <Alert className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20">
              <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <AlertDescription className="text-blue-800 dark:text-blue-200">
                {t('members.member_information_has_been_autopopulated', 'Member information has been auto-populated from the profile. Please verify and update as needed.')}
              </AlertDescription>
            </Alert>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('members.first_name', 'First Name')} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="first_name"
                  value={formData.member_info.first_name}
                  onChange={(e) => updateMemberInfo('first_name', e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('members.last_name', 'Last Name')} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="last_name"
                  value={formData.member_info.last_name}
                  onChange={(e) => updateMemberInfo('last_name', e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date_of_birth" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('members.date_of_birth', 'Date of Birth')} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="date_of_birth"
                  type="date"
                  value={formData.member_info.date_of_birth}
                  onChange={(e) => updateMemberInfo('date_of_birth', e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ssn_last_4" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('members.ssn_last_4_digits', 'SSN (Last 4 digits)')}
                </Label>
                <Input
                  id="ssn_last_4"
                  value={formData.member_info.ssn_last_4 || ''}
                  onChange={(e) => updateMemberInfo('ssn_last_4', e.target.value)}
                  maxLength={4}
                  pattern="[0-9]*"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone_number" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('members.phone_number', 'Phone Number')}
                </Label>
                <Input
                  id="phone_number"
                  value={formData.member_info.phone_number}
                  onChange={(e) => updateMemberInfo('phone_number', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('members.email', 'Email')}
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.member_info.email}
                  onChange={(e) => updateMemberInfo('email', e.target.value)}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="preferred_language" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('members.preferred_language', 'Preferred Language')}
                </Label>
                <Select
                  value={formData.member_info.preferred_language}
                  onValueChange={(value) => updateMemberInfo('preferred_language', value)}
                >
                  <SelectTrigger id="preferred_language">
                    <SelectValue placeholder={t('members.preferred_language_1', 'Preferred Language')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="English">{t('members.english', 'English')}</SelectItem>
                    <SelectItem value="Spanish">{t('members.spanish', 'Spanish')}</SelectItem>
                    <SelectItem value="Chinese">{t('members.chinese', 'Chinese')}</SelectItem>
                    <SelectItem value="Vietnamese">{t('members.vietnamese', 'Vietnamese')}</SelectItem>
                    <SelectItem value="Korean">{t('members.korean', 'Korean')}</SelectItem>
                    <SelectItem value="Other">{t('members.other', 'Other')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="state_code" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('members.state', 'State')} <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.state_code}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, state_code: value }))}
                >
                  <SelectTrigger id="state_code">
                    <SelectValue placeholder={t('members.state_1', 'State')} />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map((state) => (
                      <SelectItem key={state} value={state}>
                        {state}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        );

      case 1:
        // Address & Household
        return (
          <div className="grid grid-cols-1 gap-4">
            <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400">
              {t('members.address_information', 'Address Information')}
            </h3>
            <div className="space-y-2">
              <Label htmlFor="street_address" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('members.street_address', 'Street Address')} <span className="text-red-500">*</span>
              </Label>
              <Input
                id="street_address"
                value={formData.address.street_address}
                onChange={(e) => updateAddress('street_address', e.target.value)}
                required
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-6 gap-4">
              <div className="space-y-2 sm:col-span-3">
                <Label htmlFor="city" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('members.city', 'City')} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="city"
                  value={formData.address.city}
                  onChange={(e) => updateAddress('city', e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="state" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('members.state_2', 'State')} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="state"
                  value={formData.address.state}
                  onChange={(e) => updateAddress('state', e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2 sm:col-span-1">
                <Label htmlFor="zip_code" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('members.zip_code', 'Zip Code')} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="zip_code"
                  value={formData.address.zip_code}
                  onChange={(e) => updateAddress('zip_code', e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="county" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('members.county_optional', 'County (Optional)')}
              </Label>
              <Input
                id="county"
                value={formData.address.county || ''}
                onChange={(e) => updateAddress('county', e.target.value)}
              />
            </div>

            <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400 mt-6">
              {t('members.household_information', 'Household Information')}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="household_size" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('members.household_size', 'Household Size')} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="household_size"
                  type="number"
                  value={formData.household.household_size}
                  onChange={(e) => updateHousehold('household_size', parseInt(e.target.value))}
                  min={1}
                  required
                />
                <p className="text-xs text-gray-500 dark:text-gray-400">Total number of people living in the household</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="number_of_dependents" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('members.number_of_dependents', 'Number of Dependents')}
                </Label>
                <Input
                  id="number_of_dependents"
                  type="number"
                  value={formData.household.number_of_dependents}
                  onChange={(e) => updateHousehold('number_of_dependents', parseInt(e.target.value))}
                  min={0}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400">Number of dependents (children, etc.)</p>
              </div>
            </div>
          </div>
        );

      case 2:
        // Financial Information
        return (
          <div className="grid grid-cols-1 gap-4">
            <Alert className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20">
              <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <AlertDescription className="text-blue-800 dark:text-blue-200">
                {t('members.provide_household_financial_information_for', 'Provide household financial information for eligibility determination.')}
              </AlertDescription>
            </Alert>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="monthly_income" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('members.monthly_income', 'Monthly Income')} <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">$</span>
                  <Input
                    id="monthly_income"
                    type="number"
                    value={formData.financial.monthly_income}
                    onChange={(e) => updateFinancial('monthly_income', parseFloat(e.target.value))}
                    min={0}
                    step={0.01}
                    required
                    className="pl-7"
                  />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Total household monthly income</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="total_assets" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Total Assets (Optional)
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">$</span>
                  <Input
                    id="total_assets"
                    type="number"
                    value={formData.financial.total_assets || ''}
                    onChange={(e) => updateFinancial('total_assets', parseFloat(e.target.value))}
                    min={0}
                    step={0.01}
                    className="pl-7"
                  />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Savings, investments, property value</p>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="income_sources" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('members.income_sources', 'Income Sources')}
              </Label>
              <Select
                value={formData.financial.income_sources[0] || ''}
                onValueChange={(value) => {
                  const currentSources = formData.financial.income_sources;
                  if (!currentSources.includes(value)) {
                    updateFinancial('income_sources', [...currentSources, value]);
                  }
                }}
              >
                <SelectTrigger id="income_sources">
                  <SelectValue placeholder={t('members.income_sources_1', 'Income Sources')}>
                    {formData.financial.income_sources.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {formData.financial.income_sources.map((source) => (
                          <Badge key={source} variant="primary" size="sm">
                            {source.replace('_', ' ')}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      'Select income sources'
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {INCOME_SOURCES.map((source) => (
                    <SelectItem key={source} value={source}>
                      {source.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.financial.income_sources.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.financial.income_sources.map((source) => (
                    <Badge
                      key={source}
                      variant="primary"
                      size="sm"
                      className="cursor-pointer"
                      onClick={() => {
                        updateFinancial(
                          'income_sources',
                          formData.financial.income_sources.filter((s) => s !== source)
                        );
                      }}
                    >
                      {source.replace('_', ' ')} <X className="ml-1 h-3 w-3" />
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      case 3:
        // Work Requirements
        return (
          <div className="grid grid-cols-1 gap-4">
            <Alert className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20">
              <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <AlertDescription className="text-blue-800 dark:text-blue-200">
                {t('members.some_individuals_may_be_exempt', 'Some individuals may be exempt from work requirements. Select if applicable.')}
              </AlertDescription>
            </Alert>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="is_exempt"
                checked={formData.work_requirement?.is_exempt || false}
                onCheckedChange={(checked) => updateWorkRequirement('is_exempt', checked)}
              />
              <Label
                htmlFor="is_exempt"
                className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer"
              >
                {t('members.member_is_exempt_from_work', 'Member is exempt from work requirements')}
              </Label>
            </div>
            {formData.work_requirement?.is_exempt && (
              <div className="space-y-2">
                <Label htmlFor="exemption_reason" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('members.exemption_reason', 'Exemption Reason')}
                </Label>
                <Select
                  value={formData.work_requirement?.exemption_reason || ''}
                  onValueChange={(value) => updateWorkRequirement('exemption_reason', value)}
                >
                  <SelectTrigger id="exemption_reason">
                    <SelectValue placeholder={t('members.exemption_reason_1', 'Exemption Reason')} />
                  </SelectTrigger>
                  <SelectContent>
                    {EXEMPTION_REASONS.map((reason) => (
                      <SelectItem key={reason} value={reason}>
                        {reason}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {!formData.work_requirement?.is_exempt && (
              <Alert className="border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-900/20">
                <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                <AlertDescription className="text-yellow-800 dark:text-yellow-200">
                  Member will be required to complete 80 hours of qualifying activities per month to maintain
                  coverage.
                </AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label htmlFor="notes" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('members.notes_optional', 'Notes (Optional)')}
              </Label>
              <textarea
                id="notes"
                className="flex min-h-[80px] w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 dark:focus-visible:ring-teal-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                rows={3}
                value={formData.notes || ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                placeholder={t('members.additional_notes_about_this_enrollment', 'Additional notes about this enrollment...')}
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>{t('members.new_medicaid_enrollment', 'New Medicaid Enrollment')}</DialogTitle>
            <Badge variant="primary" size="sm">
              Member: {member.first_name} {member.last_name}
            </Badge>
          </div>
        </DialogHeader>

        <div className="mt-4">
          {/* Stepper */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              {steps.map((label, index) => (
                <React.Fragment key={label}>
                  <div className="flex flex-col items-center flex-1">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                        index <= activeStep
                          ? 'bg-v2accent text-white'
                          : 'bg-paper-2 text-ink-500'
                      }`}
                    >
                      {index + 1}
                    </div>
                    <div
                      className={`mt-2 text-xs text-center ${
                        index <= activeStep
                          ? 'text-gray-900 dark:text-gray-100 font-medium'
                          : 'text-gray-500 dark:text-gray-400'
                      }`}
                    >
                      {label}
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`h-0.5 flex-1 transition-colors ${
                        index < activeStep
                          ? 'bg-v2accent'
                          : 'bg-paper-2'
                      }`}
                      style={{ marginTop: '-20px' }}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {referralId && (
            <Alert className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20 mb-4">
              <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <AlertDescription className="text-blue-800 dark:text-blue-200">
                <strong>{t('members.creating_from_referral', 'Creating from Referral')}</strong>
                <br />
                This enrollment will be linked to referral ID: {referralId}. Your organization will earn a $100 milestone for successful enrollment outreach.
              </AlertDescription>
            </Alert>
          )}

          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {renderStepContent()}
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={handleClose} disabled={saving} icon={<X className="w-4 h-4" />}>
            {t('members.cancel', 'Cancel')}
          </Button>
          <div className="flex-1" />
          {activeStep > 0 && (
            <Button variant="outline" onClick={handleBack} disabled={saving} icon={<ArrowLeft className="w-4 h-4" />}>
              {t('members.back', 'Back')}
            </Button>
          )}
          {activeStep < steps.length - 1 ? (
            <Button variant="primary" onClick={handleNext} icon={<ArrowRight className="w-4 h-4" />} iconPosition="right">
              {t('members.next', 'Next')}
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={saving}
              loading={saving}
              icon={<Save className="w-4 h-4" />}
            >
              {saving ? 'Submitting...' : 'Submit Application'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
